import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  Ban,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Download,
  Eye,
  FileText,
  HelpCircle,
  Inbox,
  Languages,
  Link2,
  Link2Off,
  Loader2,
  Paperclip,
  Plus,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Undo2,
  XCircle,
} from "lucide-react";

const CLASSIFICATION_OPTIONS = [
  { value: "new_job", label: "New Job" },
  { value: "revision", label: "Revision / Change" },
  { value: "quote", label: "Quote / Estimate" },
  { value: "question", label: "General Question" },
  { value: "unclear", label: "Unclear" },
];

const STATUS_LABEL = {
  received: "Received",
  processing: "Processing",
  needs_review: "Needs Review",
  clarification: "Clarification",
  approved: "Approved",
  linked: "Linked",
  declined: "Declined",
  ignored: "Ignored",
};

const STATUS_STYLE = {
  received: "bg-[#F59E0B]/12 border-[#F59E0B]/50 text-[#F59E0B]",
  processing: "bg-blue-500/10 border-blue-500/40 text-blue-300",
  needs_review: "bg-[#FF6600]/12 border-[#FF6600]/40 text-[#FF6600]",
  clarification: "bg-purple-500/10 border-purple-500/40 text-purple-300",
  approved: "bg-emerald-500/10 border-emerald-500/40 text-emerald-300",
  linked: "bg-emerald-500/16 border-emerald-500/60 text-emerald-200",
  declined: "bg-red-500/10 border-red-500/40 text-red-300",
  ignored: "bg-neutral-500/10 border-neutral-500/40 text-neutral-300",
};

const ACTIONABLE = new Set(["received", "needs_review", "clarification"]);

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const StatusPill = ({ status }) => (
  <span
    data-testid={`intake-status-pill-${status}`}
    className={`inline-flex items-center rounded-full border text-[10px] font-bold tracking-[0.14em] uppercase px-2 py-0.5 ${
      STATUS_STYLE[status] || "border-[#262626] text-[#A3A3A3]"
    }`}
  >
    {STATUS_LABEL[status] || status}
  </span>
);

// ---------------------------------------------------------------------------
// Manual create modal
// ---------------------------------------------------------------------------

const ManualCreateModal = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState({
    sender_name: "",
    sender_email: "",
    subject: "",
    body_text: "",
    received_at: "",
    to_addresses: "",
    reviewer_notes: "",
  });
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const submit = async () => {
    if (!form.sender_email.trim()) {
      toast.error("Sender email is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        sender_name: form.sender_name.trim(),
        sender_email: form.sender_email.trim(),
        subject: form.subject.trim(),
        body_text: form.body_text,
        received_at: form.received_at ? new Date(form.received_at).toISOString() : "",
        to_addresses: form.to_addresses
          .split(/[,\s]+/)
          .map((s) => s.trim())
          .filter(Boolean),
        reviewer_notes: form.reviewer_notes,
      };
      const { data } = await api.post("/admin/email-intakes", payload);
      toast.success("Intake logged");
      onCreated(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log intake");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="intake-manual-create-modal"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm grid place-items-center p-4"
      onClick={onClose}
    >
      <div
        className="ur-card w-full max-w-2xl p-6 max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ur-eyebrow mb-2">Manual Intake</div>
        <h3 className="font-display text-2xl font-bold mb-4">Log Email Intake</h3>
        <p className="text-xs text-[#A3A3A3] mb-6">
          Log a real email you received outside the system. Original source
          fields become immutable once saved. Attachments are not accepted
          here — durable storage is not wired yet.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-xs text-[#A3A3A3]">
            Sender name
            <input
              data-testid="intake-create-sender-name"
              className="ur-input mt-1 w-full"
              value={form.sender_name}
              onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
            />
          </label>
          <label className="text-xs text-[#A3A3A3]">
            Sender email *
            <input
              data-testid="intake-create-sender-email"
              className="ur-input mt-1 w-full"
              value={form.sender_email}
              onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
              required
            />
          </label>
          <label className="text-xs text-[#A3A3A3] md:col-span-2">
            Subject
            <input
              data-testid="intake-create-subject"
              className="ur-input mt-1 w-full"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
            />
          </label>
          <label className="text-xs text-[#A3A3A3]">
            Received at
            <input
              data-testid="intake-create-received-at"
              type="datetime-local"
              className="ur-input mt-1 w-full"
              value={form.received_at}
              onChange={(e) => setForm({ ...form, received_at: e.target.value })}
            />
          </label>
          <label className="text-xs text-[#A3A3A3]">
            To addresses (comma-separated)
            <input
              data-testid="intake-create-to-addresses"
              className="ur-input mt-1 w-full"
              value={form.to_addresses}
              onChange={(e) => setForm({ ...form, to_addresses: e.target.value })}
            />
          </label>
          <label className="text-xs text-[#A3A3A3] md:col-span-2">
            Original message body
            <textarea
              data-testid="intake-create-body"
              rows={8}
              className="ur-input mt-1 w-full"
              value={form.body_text}
              onChange={(e) => setForm({ ...form, body_text: e.target.value })}
              placeholder="Paste the entire original email body here."
            />
          </label>
          <label className="text-xs text-[#A3A3A3] md:col-span-2">
            Envelope notes (optional)
            <textarea
              data-testid="intake-create-envelope-notes"
              rows={2}
              className="ur-input mt-1 w-full"
              value={form.reviewer_notes}
              onChange={(e) => setForm({ ...form, reviewer_notes: e.target.value })}
              placeholder="e.g. 'client mentioned attachments — not persisted here yet.'"
            />
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button
            data-testid="intake-create-cancel"
            className="ur-btn-secondary text-sm"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            data-testid="intake-create-submit"
            className="ur-btn-primary text-sm"
            disabled={saving}
            onClick={submit}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Log Intake
          </button>
        </div>
      </div>
    </div>
  );
};

const ATTACHMENT_STATE_STYLE = {
  stored: "bg-emerald-500/10 border-emerald-500/40 text-emerald-300",
  pending: "bg-blue-500/10 border-blue-500/40 text-blue-300",
  failed: "bg-red-500/10 border-red-500/40 text-red-300",
  rejected: "bg-amber-500/10 border-amber-500/40 text-amber-300",
};

const ATTACHMENT_STATE_LABEL = {
  stored: "Stored",
  pending: "Pending",
  failed: "Failed",
  rejected: "Rejected",
};

const ATTACHMENT_REASON_TEXT = {
  size_limit_exceeded: "File exceeded per-attachment size limit.",
  aggregate_size_limit_exceeded: "Aggregate attachment size limit reached.",
  count_limit_exceeded: "Too many attachments — only the first batch was accepted.",
  blocked_mime_type: "File type is blocked (executable / script).",
  provider_fetch_failed: "Provider download failed before durable save.",
  storage_unavailable: "Durable storage was temporarily unavailable.",
  storage_error: "Durable storage returned an error while saving.",
  no_provider_ref: "Sender-provided attachment reference was missing.",
};

const formatBytes = (n) => {
  const size = Number(n || 0);
  if (!size) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let val = size;
  while (val >= 1024 && i < units.length - 1) {
    val /= 1024;
    i += 1;
  }
  return `${val.toFixed(val >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
};

const AttachmentRow = ({ intakeId, attachment }) => {
  const [downloading, setDownloading] = useState(false);
  const state = attachment.state || "pending";
  const badge = ATTACHMENT_STATE_STYLE[state] || ATTACHMENT_STATE_STYLE.pending;
  const label = ATTACHMENT_STATE_LABEL[state] || state;
  const reasonText =
    attachment.failure_reason
      ? ATTACHMENT_REASON_TEXT[attachment.failure_reason] ||
        attachment.failure_reason
      : "";

  const onDownload = useCallback(async () => {
    setDownloading(true);
    try {
      const resp = await api.get(
        `/admin/email-intakes/${intakeId}/attachments/${attachment.attachment_id}/download`,
        { responseType: "blob" }
      );
      const blob = new Blob([resp.data], {
        type: attachment.mime_type || "application/octet-stream",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = attachment.filename || "attachment";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Download failed";
      toast.error(msg);
    } finally {
      setDownloading(false);
    }
  }, [intakeId, attachment]);

  return (
    <div
      data-testid={`intake-attachment-row-${attachment.attachment_id}`}
      className="border border-[#1F1F1F] bg-[#0A0A0A] rounded-lg p-3 flex items-start gap-3"
    >
      <FileText className="w-4 h-4 text-[#FF6600] mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div
            className="font-semibold truncate max-w-full"
            data-testid={`intake-attachment-filename-${attachment.attachment_id}`}
          >
            {attachment.filename || "attachment"}
          </div>
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] uppercase tracking-wide ${badge}`}
            data-testid={`intake-attachment-state-${attachment.attachment_id}`}
          >
            {state === "stored" ? (
              <ShieldCheck className="w-3 h-3" />
            ) : state === "failed" || state === "rejected" ? (
              <XCircle className="w-3 h-3" />
            ) : (
              <Loader2 className="w-3 h-3" />
            )}
            {label}
          </span>
        </div>
        <div className="text-[11px] text-[#7A7A7A] mt-1 flex flex-wrap gap-x-3">
          <span>{attachment.mime_type || "application/octet-stream"}</span>
          <span>{formatBytes(attachment.size)}</span>
          {attachment.sha256_hash ? (
            <span className="font-mono text-[10px]">
              sha256:{attachment.sha256_hash.slice(0, 10)}…
            </span>
          ) : null}
        </div>
        {reasonText ? (
          <div
            className="mt-2 text-[11px] text-amber-300 bg-amber-500/5 border border-amber-500/30 rounded-md px-2 py-1"
            data-testid={`intake-attachment-reason-${attachment.attachment_id}`}
          >
            {reasonText}
          </div>
        ) : null}
      </div>
      <div className="shrink-0">
        {state === "stored" ? (
          <button
            type="button"
            onClick={onDownload}
            disabled={downloading}
            data-testid={`intake-attachment-download-${attachment.attachment_id}`}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 border border-[#FF6600]/40 text-[#FF6600] rounded-md hover:bg-[#FF6600]/10 disabled:opacity-50"
          >
            {downloading ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Download className="w-3 h-3" />
            )}
            Download
          </button>
        ) : null}
      </div>
    </div>
  );
};

const AttachmentsList = ({ intakeId, attachments }) => {
  if (!attachments || attachments.length === 0) {
    return (
      <div
        data-testid="intake-detail-attachments-empty"
        className="text-[11px] text-[#5A5A5A] italic"
      >
        No attachments on this message.
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="intake-detail-attachments-list">
      {attachments.map((a) => (
        <AttachmentRow
          key={a.attachment_id || a.filename}
          intakeId={intakeId}
          attachment={a}
        />
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Detail — Original Message + Brief + Review actions
// ---------------------------------------------------------------------------

const OriginalMessage = ({ intakeId, source }) => {
  const [open, setOpen] = useState(true);
  const from = source?.from_address;
  const attachments = source?.attachments || [];
  return (
    <section
      data-testid="intake-detail-original"
      className="ur-card p-5"
    >
      <button
        className="w-full flex items-center justify-between text-left"
        onClick={() => setOpen((o) => !o)}
        data-testid="intake-detail-original-toggle"
      >
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-[#FF6600]" />
          <div className="ur-eyebrow">Original Client Message</div>
        </div>
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      {open && (
        <div className="mt-4 space-y-3 text-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="ur-eyebrow text-[10px]">From</div>
              <div>
                {from?.name ? `${from.name} <${from.address}>` : from?.address || "—"}
              </div>
            </div>
            <div>
              <div className="ur-eyebrow text-[10px]">Received</div>
              <div>{formatDate(source?.received_at)}</div>
            </div>
            <div className="md:col-span-2">
              <div className="ur-eyebrow text-[10px]">Subject</div>
              <div className="font-semibold">{source?.subject || "(no subject)"}</div>
            </div>
          </div>
          <div>
            <div className="ur-eyebrow text-[10px] mb-1">Body</div>
            <pre
              data-testid="intake-detail-body"
              className="whitespace-pre-wrap break-words bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg p-3 text-[13px] leading-relaxed max-h-96 overflow-auto"
            >
{source?.body_text || "(empty)"}
            </pre>
          </div>
          <div>
            <div className="ur-eyebrow text-[10px] mb-2 flex items-center gap-1">
              <Paperclip className="w-3 h-3" />
              Attachments{" "}
              {attachments.length ? (
                <span className="text-[#7A7A7A]">({attachments.length})</span>
              ) : null}
            </div>
            <AttachmentsList intakeId={intakeId} attachments={attachments} />
          </div>
          <div className="text-[10px] text-[#5A5A5A] italic">
            Source fields above are immutable — corrections happen on the Canonical Brief.
          </div>
        </div>
      )}
    </section>
  );
};

const EMPTY_BRIEF_FORM = {
  summary: "",
  deliverables: "",
  deadline: "",
  dimensions: "",
  quantities: "",
  pricing_mentioned: "",
  currency_mentioned: "",
  client_reference: "",
  project_reference: "",
  supplied_assets: "",
  special_instructions: "",
  missing_information: "",
  es_summary: "",
  es_deliverables: "",
  es_deadline: "",
};

const CanonicalBriefPanel = ({ intake, briefs, onSaved }) => {
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [form, setForm] = useState(EMPTY_BRIEF_FORM);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [showSpanish, setShowSpanish] = useState(
    intake?.human_review?.preferred_brief_language === "es"
  );
  const [aiBusy, setAiBusy] = useState(false);
  const [regenReason, setRegenReason] = useState("");

  const currentBrief = useMemo(() => {
    if (!briefs?.length) return null;
    if (selectedVersion !== null) {
      return briefs.find((b) => b.version === selectedVersion) || briefs[briefs.length - 1];
    }
    return briefs[briefs.length - 1];
  }, [briefs, selectedVersion]);

  const currentIsAI =
    currentBrief && (currentBrief.provenance || {}).generator === "ai";

  useEffect(() => {
    if (!currentBrief) {
      setForm(EMPTY_BRIEF_FORM);
      return;
    }
    const en = currentBrief.canonical_en || {};
    const extra = en.extra_fields || {};
    const es = currentBrief.derived_es || null;
    const fv = (f) => (f && f.value != null ? String(f.value) : "");
    setForm({
      summary: en.summary || "",
      deliverables: fv(en.deliverables),
      deadline: fv(en.deadline),
      dimensions: fv(en.dimensions),
      quantities: fv(en.quantities),
      pricing_mentioned: fv(en.pricing_mentioned),
      currency_mentioned: fv(en.currency_mentioned),
      client_reference: fv(en.client_reference),
      project_reference: fv(en.project_reference),
      supplied_assets: fv(extra.supplied_assets),
      special_instructions: fv(extra.special_instructions),
      missing_information: fv(extra.missing_information),
      es_summary: es?.summary || "",
      es_deliverables: es?.deliverables_text || "",
      es_deadline: es?.deadline_text || "",
    });
  }, [currentBrief]);

  const generateAI = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(
        `/admin/email-intakes/${intake.id}/generate-brief`,
        { reason: "" }
      );
      if (data.idempotent) {
        toast.success("Existing AI brief already up to date");
      } else {
        toast.success("AI brief generated — review before approving");
      }
      onSaved();
    } catch (e) {
      const msg = e?.response?.data?.detail || "AI generation failed";
      toast.error(msg);
    } finally {
      setAiBusy(false);
    }
  };

  const regenerateAI = async () => {
    if (!regenReason.trim()) {
      toast.error("Reason is required for regeneration");
      return;
    }
    setAiBusy(true);
    try {
      await api.post(`/admin/email-intakes/${intake.id}/regenerate-brief`, {
        reason: regenReason,
      });
      toast.success("AI brief regenerated (new version)");
      setRegenReason("");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regenerate failed");
    } finally {
      setAiBusy(false);
    }
  };

  const submit = async () => {
    setSaving(true);
    try {
      const field = (value) =>
        value && value.trim()
          ? { value: value.trim(), confidence: 1.0, notes: "" }
          : null;
      const canonical_en = {
        summary: form.summary || "",
        deliverables: field(form.deliverables),
        deadline: field(form.deadline),
        dimensions: field(form.dimensions),
        quantities: field(form.quantities),
        pricing_mentioned: field(form.pricing_mentioned),
        currency_mentioned: field(form.currency_mentioned),
        client_reference: field(form.client_reference),
        project_reference: field(form.project_reference),
        supplied_assets: field(form.supplied_assets),
        special_instructions: field(form.special_instructions),
        missing_information: field(form.missing_information),
      };
      // Strip null field payloads
      Object.keys(canonical_en).forEach((k) => {
        if (canonical_en[k] === null) delete canonical_en[k];
      });
      const derived_es =
        showSpanish && (form.es_summary || form.es_deliverables || form.es_deadline)
          ? {
              summary: form.es_summary || "",
              deliverables_text: form.es_deliverables || "",
              deadline_text: form.es_deadline || "",
              dimensions_text: "",
              quantities_text: "",
              pricing_text: "",
            }
          : null;
      const payload = { canonical_en };
      if (derived_es) payload.derived_es = derived_es;
      await api.post(`/admin/email-intakes/${intake.id}/briefs`, payload);
      toast.success("New brief version saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const approveBrief = async () => {
    if (!currentBrief) return;
    setApproving(true);
    try {
      await api.post(
        `/admin/email-intakes/${intake.id}/briefs/${currentBrief.id}/approve`,
        { note: "" }
      );
      toast.success(`Brief v${currentBrief.version} approved`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setApproving(false);
    }
  };

  return (
    <section data-testid="intake-detail-brief" className="ur-card p-5">
      {/* AI assistant strip — never gates approval, always advisory */}
      {!briefs?.length ? (
        <div
          data-testid="intake-ai-generate-strip"
          className="mb-4 bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 flex items-center justify-between gap-3"
        >
          <div className="flex items-center gap-2 text-xs">
            <Sparkles className="w-4 h-4 text-purple-300" />
            <div>
              <div className="font-semibold text-purple-200">AI Assist available</div>
              <div className="text-[#A3A3A3]">
                Draft the canonical brief from the client's email. You'll review it before it counts.
              </div>
            </div>
          </div>
          <button
            data-testid="intake-ai-generate-btn"
            onClick={generateAI}
            disabled={aiBusy}
            className="ur-btn-secondary text-xs whitespace-nowrap"
          >
            {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Generate AI Brief
          </button>
        </div>
      ) : currentIsAI && currentBrief.approval_state === "draft" ? (
        <div
          data-testid="intake-ai-suggested-banner"
          className="mb-4 bg-purple-500/10 border border-purple-500/40 rounded-lg p-3 text-xs"
        >
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-purple-300" />
            <span className="font-bold text-purple-200 uppercase tracking-widest text-[10px]">
              AI Suggested — Not Approved
            </span>
          </div>
          <div className="text-[#A3A3A3]">
            Every field below is a suggestion. Review, correct anything the client didn't
            actually say, and approve when ready. Missing facts stay explicitly missing.
          </div>
          {(currentBrief.provenance?.ambiguities || []).length > 0 && (
            <ul className="mt-2 list-disc pl-4 space-y-0.5">
              {currentBrief.provenance.ambiguities.map((a, i) => (
                <li key={i} className="text-purple-200">{a}</li>
              ))}
            </ul>
          )}
          {(currentBrief.provenance?.date_flags || []).length > 0 && (
            <ul
              data-testid="intake-ai-date-flags"
              className="mt-2 list-disc pl-4 space-y-0.5"
            >
              {currentBrief.provenance.date_flags.map((a, i) => (
                <li key={i} className="text-yellow-300">{a}</li>
              ))}
            </ul>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input
              data-testid="intake-ai-regenerate-reason"
              className="ur-input text-[11px] py-1 flex-1 min-w-[180px]"
              placeholder="Reason to regenerate (required)"
              value={regenReason}
              onChange={(e) => setRegenReason(e.target.value)}
            />
            <button
              data-testid="intake-ai-regenerate-btn"
              onClick={regenerateAI}
              disabled={aiBusy || !regenReason.trim()}
              className="text-[11px] px-3 py-1 rounded-full border border-purple-500/50 text-purple-200 hover:bg-purple-500/20 disabled:opacity-50"
            >
              {aiBusy ? (
                <Loader2 className="w-3 h-3 animate-spin inline mr-1" />
              ) : (
                <RefreshCw className="w-3 h-3 inline mr-1" />
              )}
              Regenerate
            </button>
          </div>
        </div>
      ) : null}

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#FF6600]" />
          <div className="ur-eyebrow">Canonical Brief</div>
          {currentBrief && (
            <span
              data-testid="intake-brief-version-tag"
              className="inline-flex items-center rounded-full bg-[#1A1A1A] border border-[#262626] text-[10px] font-bold px-2 py-0.5"
            >
              v{currentBrief.version}
              {currentIsAI && (
                <Sparkles
                  data-testid="intake-brief-ai-marker"
                  className="w-3 h-3 text-purple-300 ml-1"
                />
              )}
              {currentBrief.approval_state === "approved" && (
                <ShieldCheck className="w-3 h-3 text-emerald-400 ml-1" />
              )}
              {currentBrief.approval_state === "superseded" && (
                <span className="text-[#5A5A5A] ml-1">superseded</span>
              )}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {briefs?.length > 0 && (
            <select
              data-testid="intake-brief-version-select"
              className="ur-input py-1 text-xs"
              value={currentBrief?.version || ""}
              onChange={(e) => setSelectedVersion(Number(e.target.value))}
            >
              {briefs.map((b) => (
                <option key={b.id} value={b.version}>
                  v{b.version} · {b.approval_state}
                </option>
              ))}
            </select>
          )}
          <button
            data-testid="intake-brief-toggle-spanish"
            onClick={() => setShowSpanish((v) => !v)}
            className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border ${
              showSpanish
                ? "bg-[#FF6600] border-[#FF6600] text-white"
                : "border-[#262626] text-[#A3A3A3] hover:border-[#FF6600]"
            }`}
          >
            <Languages className="w-3.5 h-3.5" />
            {showSpanish ? "Español" : "English"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
        <label className="md:col-span-2 text-[#A3A3A3]">
          Summary
          <textarea
            data-testid="intake-brief-summary"
            rows={2}
            className="ur-input mt-1 w-full"
            value={form.summary}
            onChange={(e) => setForm({ ...form, summary: e.target.value })}
          />
        </label>
        {[
          ["deliverables", "Deliverables"],
          ["deadline", "Deadline"],
          ["dimensions", "Dimensions"],
          ["quantities", "Quantities"],
          ["pricing_mentioned", "Pricing mentioned"],
          ["currency_mentioned", "Currency mentioned"],
          ["client_reference", "Client reference"],
          ["project_reference", "Project reference"],
          ["supplied_assets", "Supplied assets"],
          ["special_instructions", "Special instructions"],
        ].map(([key, label]) => (
          <label key={key} className="text-[#A3A3A3]">
            {label}
            <input
              data-testid={`intake-brief-${key}`}
              className="ur-input mt-1 w-full"
              value={form[key]}
              onChange={(e) => setForm({ ...form, [key]: e.target.value })}
            />
          </label>
        ))}
        <label className="md:col-span-2 text-[#A3A3A3]">
          Missing information / follow-up needed
          <textarea
            data-testid="intake-brief-missing"
            rows={2}
            className="ur-input mt-1 w-full"
            value={form.missing_information}
            onChange={(e) => setForm({ ...form, missing_information: e.target.value })}
          />
        </label>
      </div>

      {showSpanish && (
        <div className="mt-4 border-t border-[#1F1F1F] pt-4">
          <div className="ur-eyebrow text-[10px] mb-2 flex items-center gap-2">
            Español
            {currentBrief?.derived_es?.stale && (
              <span
                data-testid="intake-brief-spanish-stale"
                className="inline-flex items-center rounded-full bg-red-500/10 border border-red-500/40 text-red-300 text-[10px] font-bold px-2 py-0.5"
              >
                Stale — English updated
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <label className="md:col-span-2 text-[#A3A3A3]">
              Resumen
              <textarea
                data-testid="intake-brief-es-summary"
                rows={2}
                className="ur-input mt-1 w-full"
                value={form.es_summary}
                onChange={(e) => setForm({ ...form, es_summary: e.target.value })}
              />
            </label>
            <label className="text-[#A3A3A3]">
              Entregables
              <input
                data-testid="intake-brief-es-deliverables"
                className="ur-input mt-1 w-full"
                value={form.es_deliverables}
                onChange={(e) => setForm({ ...form, es_deliverables: e.target.value })}
              />
            </label>
            <label className="text-[#A3A3A3]">
              Fecha límite
              <input
                data-testid="intake-brief-es-deadline"
                className="ur-input mt-1 w-full"
                value={form.es_deadline}
                onChange={(e) => setForm({ ...form, es_deadline: e.target.value })}
              />
            </label>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-4">
        <button
          data-testid="intake-brief-save"
          onClick={submit}
          disabled={saving}
          className="ur-btn-primary text-sm"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          {briefs?.length ? "Save as new version" : "Create Brief v1"}
        </button>
        {currentBrief && currentBrief.approval_state !== "approved" && currentBrief.approval_state !== "superseded" && (
          <button
            data-testid="intake-brief-approve"
            onClick={approveBrief}
            disabled={approving}
            className="ur-btn-secondary text-sm"
          >
            {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            Approve v{currentBrief.version}
          </button>
        )}
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Review / decision panel
// ---------------------------------------------------------------------------

const ReviewPanel = ({ intake, briefs, onChanged, onOpenJob }) => {
  const [classification, setClassification] = useState(
    intake?.human_review?.classification || ""
  );
  const [linkForm, setLinkForm] = useState({ ref_type: "quote", ref_id: "", note: "" });
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const call = async (path, payload = {}) => {
    setBusy(true);
    try {
      await api.post(`/admin/email-intakes/${intake.id}/${path}`, payload);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const setClass = async (val) => {
    setClassification(val);
    try {
      await api.post(`/admin/email-intakes/${intake.id}/classification`, {
        classification: val,
      });
      toast.success("Classification saved");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const status = intake.status;
  const currentBrief = briefs?.find((b) => b.id === intake.current_brief_id);
  const canApproveIntake =
    status === "needs_review" &&
    currentBrief &&
    currentBrief.approval_state === "approved";

  return (
    <section data-testid="intake-detail-review" className="ur-card p-5">
      <div className="flex items-center gap-2 mb-4">
        <HelpCircle className="w-4 h-4 text-[#FF6600]" />
        <div className="ur-eyebrow">Review / Decision</div>
      </div>

      <label className="text-xs text-[#A3A3A3]">
        Classification
        <select
          data-testid="intake-review-classification"
          className="ur-input mt-1 w-full"
          value={classification || ""}
          onChange={(e) => setClass(e.target.value)}
        >
          <option value="">— select —</option>
          {CLASSIFICATION_OPTIONS.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </label>

      {intake?.human_review?.missing_information?.length > 0 && (
        <div className="mt-4 bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-xs">
          <div className="ur-eyebrow text-[10px] text-red-300 mb-1">Missing</div>
          <ul className="list-disc pl-4 space-y-1 text-[#A3A3A3]">
            {intake.human_review.missing_information.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        </div>
      )}

      <label className="text-xs text-[#A3A3A3] mt-4 block">
        Reason / note (used for clarification, decline, ignore, reopen)
        <textarea
          data-testid="intake-review-reason"
          rows={2}
          className="ur-input mt-1 w-full"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>

      <div className="flex flex-wrap gap-2 mt-4">
        {status === "received" && (
          <button
            data-testid="intake-action-start-review"
            onClick={() => call("start-review")}
            className="ur-btn-secondary text-sm"
            disabled={busy}
          >
            Start Review
          </button>
        )}
        {canApproveIntake && (
          <button
            data-testid="intake-action-approve"
            onClick={() => call("approve-intake", { note: reason })}
            className="ur-btn-primary text-sm"
            disabled={busy}
          >
            <CheckCircle2 className="w-4 h-4" /> Approve Intake
          </button>
        )}
        {status === "needs_review" && (
          <button
            data-testid="intake-action-clarification"
            onClick={() =>
              call("transition", { target: "clarification", reason })
            }
            className="ur-btn-secondary text-sm"
            disabled={busy || !reason.trim()}
          >
            Request Clarification
          </button>
        )}
        {status === "clarification" && (
          <button
            data-testid="intake-action-resume-review"
            onClick={() => call("transition", { target: "needs_review", reason })}
            className="ur-btn-secondary text-sm"
            disabled={busy}
          >
            <Undo2 className="w-4 h-4" /> Resume Review
          </button>
        )}
        {status === "approved" && (
          <>
            <button
              data-testid="intake-action-reopen"
              onClick={() => call("reopen", { reason })}
              className="ur-btn-secondary text-sm"
              disabled={busy || !reason.trim()}
            >
              <Undo2 className="w-4 h-4" /> Reopen Review
            </button>
            <button
              data-testid="intake-action-prepare-order"
              onClick={() => call("prepare-order", { note: reason })}
              className="ur-btn-secondary text-sm"
              disabled={busy}
            >
              Prepare Order
            </button>
            {!intake?.linked_ref?.ref_id && (
              <button
                data-testid="intake-action-create-job"
                onClick={async () => {
                  setBusy(true);
                  try {
                    const { data } = await api.post(
                      `/admin/email-intakes/${intake.id}/create-job`
                    );
                    toast.success(data?.existing ? "Job already linked" : "Job created");
                    onChanged();
                    const jid = data?.job?.id;
                    if (jid) onOpenJob?.(jid);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Create Job failed");
                  } finally {
                    setBusy(false);
                  }
                }}
                className="ur-btn-primary text-sm"
                disabled={busy}
              >
                <Sparkles className="w-4 h-4" /> Create Job
              </button>
            )}
          </>
        )}
        {status === "linked" && (
          <button
            data-testid="intake-action-reopen-unlink"
            onClick={() => call("reopen-unlink", { reason })}
            className="ur-btn-secondary text-sm"
            disabled={busy || !reason.trim()}
          >
            <Link2Off className="w-4 h-4" /> Reopen &amp; Unlink
          </button>
        )}
        {(status === "received" ||
          status === "needs_review" ||
          status === "clarification") && (
          <>
            <button
              data-testid="intake-action-ignore"
              onClick={() =>
                call("transition", { target: "ignored", reason })
              }
              className="text-sm px-4 py-2 rounded-full border border-[#262626] text-[#A3A3A3] hover:border-red-500"
              disabled={busy}
            >
              Ignore
            </button>
            <button
              data-testid="intake-action-decline"
              onClick={() =>
                call("transition", { target: "declined", reason })
              }
              className="text-sm px-4 py-2 rounded-full border border-red-500/40 text-red-300 hover:bg-red-500/10"
              disabled={busy || !reason.trim()}
            >
              <Ban className="w-4 h-4 inline" /> Decline
            </button>
          </>
        )}
      </div>

      {status === "approved" && (
        <div className="mt-4 border-t border-[#1F1F1F] pt-4">
          <div className="ur-eyebrow text-[10px] mb-2">Link to Existing</div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <select
              data-testid="intake-link-ref-type"
              className="ur-input"
              value={linkForm.ref_type}
              onChange={(e) => setLinkForm({ ...linkForm, ref_type: e.target.value })}
            >
              <option value="quote">Quote</option>
              <option value="order">Order</option>
            </select>
            <input
              data-testid="intake-link-ref-id"
              className="ur-input col-span-2"
              placeholder="e.g. sqreq_… or ord_…"
              value={linkForm.ref_id}
              onChange={(e) => setLinkForm({ ...linkForm, ref_id: e.target.value })}
            />
          </div>
          <button
            data-testid="intake-action-link"
            onClick={() => call("link", { ...linkForm, note: reason })}
            className="ur-btn-primary text-sm mt-2"
            disabled={busy || !linkForm.ref_id.trim()}
          >
            <Link2 className="w-4 h-4" /> Link
          </button>
        </div>
      )}

      {intake?.linked_ref?.ref_id && (
        <div
          data-testid="intake-linked-ref-display"
          className="mt-4 bg-emerald-500/10 border border-emerald-500/40 rounded-lg p-3 text-xs flex items-center gap-3"
        >
          <div className="flex-1 min-w-0">
            <div className="ur-eyebrow text-[10px] text-emerald-300">
              Linked → {intake.linked_ref.ref_type}
            </div>
            <div className="font-mono truncate">{intake.linked_ref.ref_id}</div>
          </div>
          {intake.linked_ref.ref_type === "job" && (
            <button
              data-testid="intake-action-view-job"
              onClick={() => onOpenJob?.(intake.linked_ref.ref_id)}
              className="ur-btn-secondary text-xs py-1.5 px-3 shrink-0"
            >
              View Job
            </button>
          )}
        </div>
      )}

      <details className="mt-4">
        <summary className="ur-eyebrow text-[10px] cursor-pointer">
          Status history
        </summary>
        <ul className="mt-2 space-y-1 text-[11px] text-[#A3A3A3]">
          {(intake.status_history || []).map((h, i) => (
            <li key={i} className="border-l border-[#262626] pl-2">
              <span className="text-white font-semibold">{h.status}</span>
              <span className="ml-2">{formatDate(h.at)}</span>
              <span className="ml-2 text-[#5A5A5A]">by {h.actor}</span>
              {h.reason && <span className="ml-2">— {h.reason}</span>}
            </li>
          ))}
        </ul>
      </details>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Detail view (loads briefs + shows all sections)
// ---------------------------------------------------------------------------

const IntakeDetail = ({ intakeId, onBack, onListRefresh, onOpenJob }) => {
  const [state, setState] = useState({ intake: null, briefs: [] });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/email-intakes/${intakeId}`);
      setState(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load intake");
    } finally {
      setLoading(false);
    }
  }, [intakeId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onChanged = async () => {
    await refresh();
    onListRefresh();
  };

  if (loading || !state.intake) {
    return (
      <div className="text-sm text-[#A3A3A3] py-10 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  const { intake, briefs } = state;

  return (
    <div data-testid="intake-detail-view" className="space-y-4">
      <button
        data-testid="intake-detail-back"
        onClick={onBack}
        className="text-sm text-[#A3A3A3] hover:text-[#FF6600] inline-flex items-center gap-1"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Email Intake
      </button>
      <div className="flex items-center gap-3">
        <h2 className="font-display text-2xl font-bold flex-1 truncate">
          {intake.source?.subject || "(no subject)"}
        </h2>
        <StatusPill status={intake.status} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-4">
          <OriginalMessage intakeId={intake.id} source={intake.source} />
        </div>
        <div className="space-y-4">
          <CanonicalBriefPanel intake={intake} briefs={briefs} onSaved={onChanged} />
          <ReviewPanel intake={intake} briefs={briefs} onChanged={onChanged} onOpenJob={onOpenJob} />
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Root panel (list ↔ detail)
// ---------------------------------------------------------------------------

export const EmailIntakePanel = ({ onOpenJob } = {}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : "";
      const { data } = await api.get(`/admin/email-intakes${params}`);
      setRows(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load intakes");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  if (selectedId) {
    return (
      <IntakeDetail
        intakeId={selectedId}
        onBack={() => setSelectedId(null)}
        onListRefresh={load}
        onOpenJob={onOpenJob}
      />
    );
  }

  const actionableRows = rows.filter((r) => ACTIONABLE.has(r.status));

  return (
    <div data-testid="admin-email-intake-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl font-bold flex items-center gap-2">
            <Inbox className="w-5 h-5 text-[#FF6600]" /> Email Intake
          </h2>
          <p className="text-xs text-[#A3A3A3] mt-1">
            {actionableRows.length} actionable · {rows.length} total
          </p>
        </div>
        <div className="flex gap-2">
          <select
            data-testid="admin-email-intake-filter"
            className="ur-input text-sm py-2"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Actionable first</option>
            <option value="received">Received</option>
            <option value="processing">Processing</option>
            <option value="needs_review">Needs Review</option>
            <option value="clarification">Clarification</option>
            <option value="approved">Approved</option>
            <option value="linked">Linked</option>
            <option value="declined">Declined</option>
            <option value="ignored">Ignored</option>
          </select>
          <button
            data-testid="admin-email-intake-new"
            onClick={() => setShowCreate(true)}
            className="ur-btn-primary text-sm py-2.5 px-5"
          >
            <Plus className="w-4 h-4" /> Log Intake
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-[#A3A3A3] py-8 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div
          data-testid="admin-email-intake-empty"
          className="ur-card p-10 text-center text-sm text-[#A3A3A3]"
        >
          <Inbox className="w-10 h-10 mx-auto mb-3 text-[#5A5A5A]" />
          <div className="font-semibold text-white mb-1">No intakes yet</div>
          <div>
            Log your first email manually — inbound provider integration lands
            in a later pass.
          </div>
        </div>
      ) : (
        <div
          data-testid="admin-email-intake-list"
          className="grid gap-2"
        >
          {rows.map((row) => (
            <button
              key={row.id}
              data-testid={`admin-email-intake-row-${row.id}`}
              onClick={() => setSelectedId(row.id)}
              className="ur-card p-4 text-left hover:border-[#FF6600] transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-semibold truncate">
                      {row.source?.subject || "(no subject)"}
                    </span>
                    <StatusPill status={row.status} />
                    {row.source?.provider === "resend_inbound" && (
                      <span
                        data-testid="intake-provider-resend"
                        className="inline-flex items-center rounded-full bg-blue-500/10 border border-blue-500/40 text-blue-300 text-[10px] font-bold px-2 py-0.5"
                      >
                        Email / Resend
                      </span>
                    )}
                    {row.auto_ai_state === "pending" && (
                      <span
                        data-testid={`intake-auto-ai-pill-${row.id}`}
                        className="inline-flex items-center rounded-full bg-amber-500/10 border border-amber-500/40 text-amber-300 text-[10px] font-bold px-2 py-0.5"
                      >
                        Auto-AI: Queued
                      </span>
                    )}
                    {row.auto_ai_state === "running" && (
                      <span
                        data-testid={`intake-auto-ai-pill-${row.id}`}
                        className="inline-flex items-center rounded-full bg-sky-500/10 border border-sky-500/40 text-sky-300 text-[10px] font-bold px-2 py-0.5"
                      >
                        Auto-AI: Processing
                      </span>
                    )}
                    {row.auto_ai_state === "failed" && (
                      <span
                        data-testid={`intake-auto-ai-pill-${row.id}`}
                        className="inline-flex items-center rounded-full bg-red-500/10 border border-red-500/40 text-red-300 text-[10px] font-bold px-2 py-0.5"
                      >
                        Auto-AI: Failed
                      </span>
                    )}
                    {row.human_review?.classification && (
                      <span className="inline-flex items-center rounded-full bg-[#1A1A1A] border border-[#262626] text-[10px] font-bold px-2 py-0.5">
                        {row.human_review.classification.replace("_", " ")}
                      </span>
                    )}
                    {row.linked_ref?.ref_id && (
                      <span className="inline-flex items-center rounded-full bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-[10px] font-bold px-2 py-0.5">
                        <Link2 className="w-3 h-3 mr-1" />
                        {row.linked_ref.ref_type}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[#A3A3A3] mt-1 truncate">
                    {row.source?.from_address?.address || "—"} ·{" "}
                    {formatDate(row.source?.received_at)}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-[#5A5A5A] shrink-0" />
              </div>
            </button>
          ))}
        </div>
      )}

      <ManualCreateModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => load()}
      />
    </div>
  );
};
